'use client';

import { Plus } from 'lucide-react';
import { useState } from 'react';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@/components/ui';
import { CIToken, useCITokens } from '@/lib/query/useCITokens';
import { useRevokeCIToken } from '@/lib/query/useRevokeCIToken';
import { theme } from '@/lib/ui/theme';

import { CreateCITokenModal } from './CreateCITokenModal';
import { RevokeCITokenModal } from './RevokeCITokenModal';

interface CITokensCardProps {
  isAdmin: boolean;
}

type TokenStatus = 'active' | 'expired' | 'revoked';

function statusOf(token: CIToken): TokenStatus {
  if (token.revokedAt) return 'revoked';
  if (token.expiresAt && new Date(token.expiresAt).getTime() < Date.now()) {
    return 'expired';
  }
  return 'active';
}

function formatDate(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_BADGE: Record<
  TokenStatus,
  { label: string; variant: 'success' | 'warning' | 'secondary' }
> = {
  active: { label: 'Active', variant: 'success' },
  expired: { label: 'Expired', variant: 'warning' },
  revoked: { label: 'Revoked', variant: 'secondary' },
};

export function CITokensCard({ isAdmin }: CITokensCardProps) {
  // Non-admins never call the admin-only list endpoint.
  const {
    data: tokens,
    isLoading,
    isError,
  } = useCITokens({ enabled: isAdmin });
  const revokeMutation = useRevokeCIToken();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<CIToken | null>(null);

  // Informational variant: a non-admin developer who needs CI auth can see the
  // feature exists and knows to ask an organization admin. No data is fetched.
  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>CI tokens</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p
            className="text-muted-foreground text-sm"
            style={{ fontFamily: theme.fonts.body }}
          >
            CI tokens let you run <code>mind review</code> headlessly in CI or
            any non-interactive environment. They are managed by organization
            admins. Ask an admin to generate one, then see the{' '}
            <a
              href="/docs#ci"
              className="underline"
              style={{ textDecorationColor: theme.colors.semantic.info }}
            >
              CI docs
            </a>{' '}
            for setup.
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleConfirmRevoke = async () => {
    if (!tokenToRevoke) return;
    try {
      await revokeMutation.mutateAsync(tokenToRevoke.id);
    } finally {
      setTokenToRevoke(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>CI tokens</CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <p
          className="text-muted-foreground mb-4 text-sm"
          style={{ fontFamily: theme.fonts.body }}
        >
          Organization-scoped, review-only credentials for running{' '}
          <code>mind review</code> headlessly. See the{' '}
          <a
            href="/docs#ci"
            className="underline"
            style={{ textDecorationColor: theme.colors.semantic.info }}
          >
            CI docs
          </a>
          .
        </p>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full rounded-md" />
            <Skeleton className="h-12 w-full rounded-md" />
          </div>
        ) : isError ? (
          <p className="text-destructive text-sm">Failed to load CI tokens.</p>
        ) : !tokens || tokens.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No CI tokens yet. Create one to authenticate{' '}
            <code>mind review</code> in CI.
          </p>
        ) : (
          <ul className="divide-border divide-y">
            {tokens.map((token) => {
              const status = statusOf(token);
              const badge = STATUS_BADGE[status];
              return (
                <li
                  key={token.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{token.name}</span>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </div>
                    <p className="text-muted-foreground font-mono text-xs">
                      {token.tokenPrefix}...
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Created {formatDate(token.createdAt)} · Expires{' '}
                      {formatDate(token.expiresAt)} · Last used{' '}
                      {formatDate(token.lastUsedAt)}
                    </p>
                  </div>
                  {status === 'active' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setTokenToRevoke(token)}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <Button
          variant="outline"
          className="mt-4"
          onClick={() => setIsCreateOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create token
        </Button>
      </CardContent>

      <CreateCITokenModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
      <RevokeCITokenModal
        isOpen={tokenToRevoke !== null}
        onClose={() => setTokenToRevoke(null)}
        token={tokenToRevoke}
        onConfirm={handleConfirmRevoke}
        isPending={revokeMutation.isPending}
      />
    </Card>
  );
}
